import React, { useEffect, useState } from "react";
import axios from "axios";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { formatDate } from "../helper/helper.js";
import { toast } from "react-toastify";

import Loader from "../components/Loader";

const NotificationManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Send notification states
  const [showModal, setShowModal] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [sendToAll, setSendToAll] = useState(true);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [merchantSearchQuery, setMerchantSearchQuery] = useState("");

  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteNotificationId, setDeleteNotificationId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/notification/list`,
        {
          params: {
            pageNo: currentPage,
            limitVal: 10,
            search: searchQuery,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("resssss", response);

      setNotifications(response?.data?.notifications);
      setTotalPages(response?.data?.totalPages);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [currentPage, searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  // Fetch merchants for selection
  const fetchMerchants = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/merchants/list`,
        {
          params: {
            pageNo: 1,
            limitVal: 1000,
            search: "",
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMerchants(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching merchants:", error);
    }
  };

  // Open modal and fetch merchants
  const handleOpenModal = () => {
    setShowModal(true);
    fetchMerchants();
  };

  // Close modal and reset form
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMerchants([]);
    setSendToAll(true);
    setNotificationMessage("");
    setMerchantSearchQuery("");
  };

  // Handle merchant selection
  const handleMerchantSelect = (merchantId) => {
    setSelectedMerchants((prev) =>
      prev.includes(merchantId)
        ? prev.filter((id) => id !== merchantId)
        : [...prev, merchantId]
    );
  };

  // Select all merchants
  const handleSelectAllMerchants = () => {
    if (selectedMerchants.length === filteredMerchants.length) {
      setSelectedMerchants([]);
    } else {
      setSelectedMerchants(filteredMerchants.map((m) => m._id));
    }
  };

  // Filter merchants based on search
  const filteredMerchants = merchants.filter(
    (merchant) =>
      merchant?.name?.toLowerCase().includes(merchantSearchQuery.toLowerCase()) ||
      merchant?.email?.toLowerCase().includes(merchantSearchQuery.toLowerCase())
  );

  // Open delete confirmation modal
  const openDeleteModal = (notificationId) => {
    setDeleteNotificationId(notificationId);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteNotificationId(null);
  };

  // Delete notification
  const handleDeleteNotification = async () => {
    if (!deleteNotificationId) return;

    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/notification/delete/${deleteNotificationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Notification deleted successfully!");
      closeDeleteModal();
      fetchNotifications();
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error(error?.response?.data?.message || "Failed to delete notification");
    } finally {
      setIsDeleting(false);
    }
  };

  // Send notification
  const handleSendNotification = async () => {
    if (!notificationMessage.trim()) {
      toast.error("Please enter notification message");
      return;
    }
    if (!sendToAll && selectedMerchants.length === 0) {
      toast.error("Please select at least one merchant");
      return;
    }

    setIsSending(true);
    try {
      const payload = {
        message: notificationMessage,
        sendToAll: sendToAll,
        merchantIds: sendToAll ? [] : selectedMerchants,
      };

      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/notification/send`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Notification sent successfully!");
      handleCloseModal();
      fetchNotifications();
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error(error?.response?.data?.message || "Failed to send notification");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 d-flex justify-content-between align-items-center">
              <div className="col-sm-6">
                <h1 className="m-0">Notification Management</h1>
              </div>
              <div>
                <button
                  className="btn btn-primary"
                  onClick={handleOpenModal}
                >
                  <i className="fas fa-bell mr-2"></i>
                  Send Notification
                </button>
              </div>
            </div>
          </div>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    {/* Search Input */}
                    <div className="notification-search-container" style={{ marginBottom: "20px" }}>
                      <div className="notification-search-wrapper">
                        <i className="fas fa-search notification-search-icon"></i>
                        <input
                          type="text"
                          className="notification-search-input"
                          placeholder="Search notifications by message..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                        />
                        {searchQuery && (
                          <button
                            className="notification-search-clear"
                            onClick={() => setSearchQuery("")}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    {loading ? (
                      <Loader />
                    ) : (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Merchant Name</th>
                            <th>Email</th>
                            <th>Notification</th>
                            <th>Time Stamp</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {notifications?.length > 0 ? (
                            notifications.map((notification, index) => (
                              <tr key={index}>
                                <td>
                                  {notification?.notificationType === "ADMIN" ? (
                                    <span className="badge badge-primary" style={{ backgroundColor: "#2196F3", padding: "5px 10px", borderRadius: "4px", color: "white" }}>
                                      All Merchants
                                    </span>
                                  ) : (
                                    <span className="badge badge-success" style={{ backgroundColor: "#4caf50", padding: "5px 10px", borderRadius: "4px", color: "white" }}>
                                      Specific
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {notification?.notificationType === "ADMIN"
                                    ? "All Merchants"
                                    : notification?.merchantDetail?.name || "-"}
                                </td>
                                <td>
                                  {notification?.notificationType === "ADMIN"
                                    ? "-"
                                    : notification?.merchantDetail?.email || "-"}
                                </td>
                                <td>{notification?.message}</td>
                                <td>{formatDate(notification?.createdAt)}</td>
                                <td>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => openDeleteModal(notification._id)}
                                    title="Delete Notification"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6">
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100%",
                                    marginTop: "20px",
                                  }}
                                >
                                  <p>Data not found</p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="notification-pagination">
                    <button
                      className="notification-pagination-btn"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      className="notification-pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>

                    {(() => {
                      const pages = [];
                      let startPage = Math.max(1, currentPage - 2);
                      let endPage = Math.min(totalPages, currentPage + 2);

                      if (currentPage <= 3) {
                        endPage = Math.min(5, totalPages);
                      }
                      if (currentPage >= totalPages - 2) {
                        startPage = Math.max(1, totalPages - 4);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            className={`notification-pagination-btn ${currentPage === i ? 'active' : ''}`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}

                    <button
                      className="notification-pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      className="notification-pagination-btn"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>

                    <span className="notification-pagination-info">
                      Page {currentPage} of {totalPages}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Notification Modal */}
      {showModal && (
        <div className="notification-modal-overlay" onClick={handleCloseModal}>
          <div className="notification-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="notification-modal-header">
              <div className="notification-modal-header-content">
                <div className="notification-modal-icon">
                  <i className="fas fa-bell"></i>
                </div>
                <h5 className="notification-modal-title">Send Notification</h5>
              </div>
              <button type="button" className="notification-modal-close" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="notification-modal-body">
              {/* Notification Message */}
              <div className="notification-form-group">
                <label className="notification-form-label">
                  <i className="fas fa-comment-alt"></i>
                  Notification Message <span className="required">*</span>
                </label>
                <textarea
                  className="notification-form-textarea"
                  rows="4"
                  placeholder="Enter notification message"
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                />
              </div>

              {/* Send To Options */}
              <div className="notification-form-group">
                <label className="notification-form-label">
                  <i className="fas fa-users"></i>
                  Send To
                </label>
                <div className="notification-radio-group">
                  <label className={`notification-radio-option ${sendToAll ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="sendTo"
                      checked={sendToAll}
                      onChange={() => setSendToAll(true)}
                    />
                    <span className="notification-radio-checkmark"></span>
                    <span className="notification-radio-text">
                      <i className="fas fa-globe"></i>
                      All Merchants
                    </span>
                  </label>
                  <label className={`notification-radio-option ${!sendToAll ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="sendTo"
                      checked={!sendToAll}
                      onChange={() => setSendToAll(false)}
                    />
                    <span className="notification-radio-checkmark"></span>
                    <span className="notification-radio-text">
                      <i className="fas fa-user-check"></i>
                      Selected Merchants
                    </span>
                  </label>
                </div>
              </div>

              {/* Merchant Selection (shown only when sendToAll is false) */}
              {!sendToAll && (
                <div className="notification-form-group notification-merchant-section">
                  <div className="notification-merchant-header">
                    <label className="notification-form-label">
                      <i className="fas fa-store"></i>
                      Select Merchants
                    </label>
                    <span className="notification-selected-count">
                      {selectedMerchants.length} selected
                    </span>
                  </div>

                  <div className="notification-merchant-search">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      placeholder="Search merchants by name or email..."
                      value={merchantSearchQuery}
                      onChange={(e) => setMerchantSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="notification-merchant-actions">
                    <button
                      type="button"
                      className="notification-select-all-btn"
                      onClick={handleSelectAllMerchants}
                    >
                      <i className={`fas ${selectedMerchants.length === filteredMerchants.length && filteredMerchants.length > 0 ? 'fa-times-circle' : 'fa-check-circle'}`}></i>
                      {selectedMerchants.length === filteredMerchants.length && filteredMerchants.length > 0
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  </div>

                  <div className="notification-merchant-list">
                    {filteredMerchants.length > 0 ? (
                      filteredMerchants.map((merchant) => (
                        <div
                          key={merchant._id}
                          className={`notification-merchant-item ${selectedMerchants.includes(merchant._id) ? 'selected' : ''}`}
                          onClick={() => handleMerchantSelect(merchant._id)}
                        >
                          <div className="notification-merchant-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedMerchants.includes(merchant._id)}
                              onChange={() => { }}
                            />
                            <span className="checkmark"></span>
                          </div>
                          <div className="notification-merchant-info">
                            <span className="notification-merchant-name">{merchant.name}</span>
                            <span className="notification-merchant-email">{merchant.email}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="notification-no-merchants">
                        <i className="fas fa-inbox"></i>
                        <p>No merchants found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="notification-modal-footer">
              <button
                type="button"
                className="notification-btn notification-btn-cancel"
                onClick={handleCloseModal}
                disabled={isSending}
              >
                <i className="fas fa-times"></i>
                Cancel
              </button>
              <button
                type="button"
                className="notification-btn notification-btn-send"
                onClick={handleSendNotification}
                disabled={isSending}
              >
                {isSending ? (
                  <>
                    <span className="notification-spinner"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i>
                    Send Notification
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3 className="delete-modal-title">Delete Notification</h3>
            <p className="delete-modal-message">
              Are you sure you want to delete this notification? This action cannot be undone.
            </p>
            <div className="delete-modal-buttons">
              <button
                type="button"
                className="delete-modal-btn delete-modal-btn-cancel"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-modal-btn delete-modal-btn-delete"
                onClick={handleDeleteNotification}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="delete-spinner"></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .notification-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
          padding: 20px;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .notification-modal-container {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 550px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          animation: slideIn 0.3s ease-out;
          display: flex;
          flex-direction: column;
        }

        .notification-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          background: #2196F3;
          color: white;
        }

        .notification-modal-header-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .notification-modal-icon {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }

        .notification-modal-title {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }

        .notification-modal-close {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .notification-modal-close:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.05);
        }

        .notification-modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .notification-form-group {
          margin-bottom: 20px;
        }

        .notification-form-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .notification-form-label i {
          color: #2196F3;
          font-size: 14px;
        }

        .notification-form-label .required {
          color: #ef4444;
        }

        .notification-form-input,
        .notification-form-textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.2s;
          background: #f9fafb;
        }

        .notification-form-input:focus,
        .notification-form-textarea:focus {
          outline: none;
          border-color: #2196F3;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        .notification-form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .notification-radio-group {
          display: flex;
          gap: 12px;
        }

        .notification-radio-option {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          background: #f9fafb;
        }

        .notification-radio-option:hover {
          border-color: #2196F3;
          background: #e3f2fd;
        }

        .notification-radio-option.active {
          border-color: #2196F3;
          background: rgba(33, 150, 243, 0.1);
        }

        .notification-radio-option input {
          display: none;
        }

        .notification-radio-checkmark {
          width: 20px;
          height: 20px;
          border: 2px solid #d1d5db;
          border-radius: 50%;
          position: relative;
          transition: all 0.2s;
        }

        .notification-radio-option.active .notification-radio-checkmark {
          border-color: #2196F3;
        }

        .notification-radio-option.active .notification-radio-checkmark::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 10px;
          height: 10px;
          background: #2196F3;
          border-radius: 50%;
        }

        .notification-radio-text {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }

        .notification-radio-text i {
          color: #2196F3;
        }

        .notification-merchant-section {
          background: #f9fafb;
          border-radius: 12px;
          padding: 16px;
          border: 1px solid #e5e7eb;
        }

        .notification-merchant-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .notification-merchant-header .notification-form-label {
          margin-bottom: 0;
        }

        .notification-selected-count {
          background: #2196F3;
          color: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .notification-merchant-search {
          position: relative;
          margin-bottom: 12px;
        }

        .notification-merchant-search i {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .notification-merchant-search input {
          width: 100%;
          padding: 10px 14px 10px 40px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          transition: all 0.2s;
        }

        .notification-merchant-search input:focus {
          outline: none;
          border-color: #2196F3;
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        .notification-merchant-actions {
          margin-bottom: 12px;
        }

        .notification-select-all-btn {
          background: white;
          border: 1px solid #e5e7eb;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #2196F3;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }

        .notification-select-all-btn:hover {
          background: #2196F3;
          color: white;
          border-color: #2196F3;
        }

        .notification-merchant-list {
          max-height: 200px;
          overflow-y: auto;
          background: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .notification-merchant-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          cursor: pointer;
          transition: all 0.2s;
          border-bottom: 1px solid #f3f4f6;
        }

        .notification-merchant-item:last-child {
          border-bottom: none;
        }

        .notification-merchant-item:hover {
          background: #e3f2fd;
        }

        .notification-merchant-item.selected {
          background: rgba(33, 150, 243, 0.1);
        }

        .notification-merchant-checkbox {
          position: relative;
          width: 20px;
          height: 20px;
        }

        .notification-merchant-checkbox input {
          opacity: 0;
          position: absolute;
        }

        .notification-merchant-checkbox .checkmark {
          position: absolute;
          top: 0;
          left: 0;
          width: 20px;
          height: 20px;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .notification-merchant-item.selected .notification-merchant-checkbox .checkmark {
          background: #2196F3;
          border-color: #2196F3;
        }

        .notification-merchant-item.selected .notification-merchant-checkbox .checkmark::after {
          content: '';
          position: absolute;
          left: 6px;
          top: 2px;
          width: 5px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        .notification-merchant-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .notification-merchant-name {
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .notification-merchant-email {
          color: #9ca3af;
          font-size: 12px;
        }

        .notification-no-merchants {
          padding: 40px 20px;
          text-align: center;
          color: #9ca3af;
        }

        .notification-no-merchants i {
          font-size: 32px;
          margin-bottom: 8px;
        }

        .notification-no-merchants p {
          margin: 0;
          font-size: 14px;
        }

        .notification-modal-footer {
          display: flex;
          gap: 12px;
          padding: 20px 24px;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .notification-btn {
          flex: 1;
          padding: 12px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          border: none;
        }

        .notification-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .notification-btn-cancel {
          background: white;
          color: #6b7280;
          border: 2px solid #e5e7eb;
        }

        .notification-btn-cancel:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .notification-btn-send {
          background: #2196F3;
          color: white;
        }

        .notification-btn-send:hover:not(:disabled) {
          background: #1976D2;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
        }

        .notification-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Scrollbar styling */
        .notification-merchant-list::-webkit-scrollbar,
        .notification-modal-body::-webkit-scrollbar {
          width: 6px;
        }

        .notification-merchant-list::-webkit-scrollbar-track,
        .notification-modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .notification-merchant-list::-webkit-scrollbar-thumb,
        .notification-modal-body::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .notification-merchant-list::-webkit-scrollbar-thumb:hover,
        .notification-modal-body::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }

        /* Delete Confirmation Modal Styles */
        .delete-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1060;
          padding: 20px;
          animation: fadeIn 0.2s ease-out;
        }

        .delete-modal-container {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 400px;
          padding: 32px;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          animation: slideIn 0.3s ease-out;
        }

        .delete-modal-icon {
          width: 64px;
          height: 64px;
          background: #fef2f2;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .delete-modal-icon i {
          font-size: 28px;
          color: #ef4444;
        }

        .delete-modal-title {
          margin: 0 0 12px;
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
        }

        .delete-modal-message {
          margin: 0 0 24px;
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
        }

        .delete-modal-buttons {
          display: flex;
          gap: 12px;
        }

        .delete-modal-btn {
          flex: 1;
          padding: 12px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          border: none;
        }

        .delete-modal-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .delete-modal-btn-cancel {
          background: #f3f4f6;
          color: #6b7280;
          border: 2px solid #e5e7eb;
        }

        .delete-modal-btn-cancel:hover:not(:disabled) {
          background: #e5e7eb;
          border-color: #d1d5db;
        }

        .delete-modal-btn-delete {
          background: #ef4444;
          color: white;
        }

        .delete-modal-btn-delete:hover:not(:disabled) {
          background: #dc2626;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }

        .delete-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        /* Search Input Styles */
        .notification-search-container {
          margin-bottom: 20px;
        }

        .notification-search-wrapper {
          position: relative;
          max-width: 400px;
        }

        .notification-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          font-size: 14px;
        }

        .notification-search-input {
          width: 100%;
          padding: 12px 40px 12px 42px;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.2s;
          background: #f9fafb;
        }

        .notification-search-input:focus {
          outline: none;
          border-color: #2196F3;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        .notification-search-input::placeholder {
          color: #9ca3af;
        }

        .notification-search-clear {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: #e5e7eb;
          border: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          transition: all 0.2s;
        }

        .notification-search-clear:hover {
          background: #d1d5db;
          color: #374151;
        }

        .notification-search-clear i {
          font-size: 10px;
        }

        /* Pagination Styles */
        .notification-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 20px;
          padding: 16px;
          flex-wrap: wrap;
        }

        .notification-pagination-btn {
          min-width: 40px;
          height: 40px;
          padding: 0 12px;
          border: 2px solid #e5e7eb;
          background: #ffffff;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .notification-pagination-btn:hover:not(:disabled) {
          border-color: #2196F3;
          color: #2196F3;
          background: #e3f2fd;
        }

        .notification-pagination-btn.active {
          background: #2196F3;
          border-color: #2196F3;
          color: white;
        }

        .notification-pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #f9fafb;
        }

        .notification-pagination-info {
          margin-left: 16px;
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default NotificationManagement;
